/**
 * Video review wall.
 *
 * Clips play muted and looping while they are on screen and pause the moment they leave,
 * so the page has motion without four videos competing for bandwidth. Nothing is
 * downloaded until a card is near the viewport: every <video> ships with preload="none"
 * and is only asked to play once it intersects.
 *
 * Tapping a card turns the sound on and hands over the native controls; only one card
 * ever has sound. Under prefers-reduced-motion nothing autoplays at all — the poster
 * stays put until someone asks for it.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Plays a video, and tries again once it actually has data.
 *
 * These clips are raw Shopify Files URLs — the original uploads, tens of megabytes, not a
 * transcoded stream — and they ship with preload="none". On a cold cache the first play()
 * is called while readyState is 0 and rejects with AbortError in about 100ms; four
 * seconds later the same video is fully buffered and still paused, because nothing tried
 * again. Measured on the live site. Waiting for canplay and retrying once fixes it.
 *
 * A second rejection is left alone: at that point the browser is refusing on purpose
 * (autoplay policy, data saver, reduced motion) and forcing it would be wrong.
 */
function playWhenReady(video) {
  if (!video) return;
  const attempt = () => video.play().catch(() => {});

  // Already has data: just play.
  if (video.readyState >= 2) return attempt();

  // Nothing has been fetched yet, so ask for it — but only when the element is genuinely
  // empty. load() rewinds to zero and cancels whatever is in flight, so calling it on a
  // video that is already loading (or already playing) restarts it. That was the bug:
  // the clip played for an instant, load() reset it, and it sat at 0.00 looking as though
  // autoplay had been refused.
  if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();

  video.addEventListener('canplay', attempt, { once: true });
  attempt();
}

class BeeulVideoWall extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('[data-beeul-clip]')];
    if (!this.cards.length) return;

    this.autoplay = this.getAttribute('autoplay') === 'true';

    for (const card of this.cards) {
      const video = card.querySelector('video');
      if (video) {
        // Safari needs this set as a property, not only as an attribute.
        video.playsInline = true;
        video.muted = true;
      }

      const toggle = card.querySelector('[data-beeul-clip-toggle]');
      toggle?.addEventListener('click', () => this.activate(card));

      const embed = card.querySelector('[data-beeul-embed]');
      if (embed) toggle?.addEventListener('click', () => this.loadEmbed(card, embed), { once: true });
    }

    if (this.autoplay && !REDUCED_MOTION.matches) this.observe();

    REDUCED_MOTION.addEventListener?.('change', (event) => {
      if (!event.matches) return;
      for (const card of this.cards) card.querySelector('video')?.pause();
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  observe() {
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target.querySelector('video');
          if (!video) continue;

          if (entry.isIntersecting) {
            playWhenReady(video);
          } else {
            video.pause();
            if (entry.target !== this.active) this.deactivate(entry.target);
          }
        }
      },
      { threshold: 0.4 }
    );

    for (const card of this.cards) this.observer.observe(card);
  }

  /**
   * Opens the clip in a modal with sound.
   *
   * The tile keeps doing what it was doing — muted, looping, on screen — and the modal
   * takes over from the same moment in the clip. Closing hands the position back, so the
   * tile carries on from where the modal stopped rather than jumping.
   *
   * GLightbox does the modal itself: focus trapping, Esc, the backdrop, arrow keys and
   * the responsive sizing that makes it a panel on a desktop and full-bleed on a phone.
   * It is MIT-licensed, vanilla and dependency-free. (Fancybox is the better-known
   * choice and was rejected on licensing: it is GPLv3 or a paid commercial licence, and
   * this is a commercial store.)
   *
   * It is fetched on the first click rather than on page load, so a visitor who never
   * opens a clip never pays for it — and if the fetch fails the tile falls back to
   * unmuting in place, which is what it did before.
   */
  async activate(card) {
    if (this.active && this.active !== card) this.deactivate(this.active);

    const video = card.querySelector('video');
    if (!video) return;

    const src = video.getAttribute('src');
    const lightbox = src ? await BeeulVideoWall.loadLightbox() : null;

    if (!lightbox) return this.activateInline(card);

    const start = video.currentTime || 0;
    const box = lightbox({
      elements: [{ href: src, type: 'video', source: 'local', width: 900 }],
      touchNavigation: false,
      loop: false,
      zoomable: false,
      draggable: false,
      // GLightbox's own video handling sets muted back to false before it starts, which
      // is the one thing a browser will refuse without a live user gesture — measured:
      // readyState 4, fully buffered, and still sitting at 0.00. Playback is driven here
      // instead: muted first, because that is always allowed, then unmuted once it is
      // genuinely running.
      autoplayVideos: false,
      openEffect: 'fade',
      closeEffect: 'fade',
    });

    // The position is tracked as the modal plays rather than read when it closes.
    // Measured: by the time the close event fires GLightbox has already removed the
    // slide, so querying for the video then returns null and the tile jumps back to
    // wherever it happened to be. Following timeupdate means the last known position
    // survives the teardown.
    let handoff = start;

    // The modal video does not exist when `open` fires — GLightbox builds the slide
    // afterwards. Querying for it there returns null, the timeupdate listener attaches to
    // nothing, and the position handed back is whatever it started as. Measured: the tile
    // came back at 0.93s after the modal had played to 5.41s. So wait for the element.
    box.on('open', async () => {
      const modalVideo = await BeeulVideoWall.waitFor('.gslide-media video');
      if (!modalVideo) return;
      try { modalVideo.currentTime = start; } catch (e) { /* not seekable yet */ }
      modalVideo.controls = true;
      modalVideo.addEventListener('timeupdate', () => {
        if (Number.isFinite(modalVideo.currentTime)) handoff = modalVideo.currentTime;
      });

      // Starts muted, then unmutes the moment it is actually playing.
      //
      // Opening the modal is asynchronous — the element has to exist before it can be
      // wired up — and by the time that finishes the click that opened it no longer
      // counts as user activation. Chrome then refuses to start an unmuted video:
      // measured with a real click on a fully buffered clip, readyState 4, still paused.
      // Muted playback is always allowed, and unmuting something already playing is not
      // gated the same way. If the unmute does stop it, the controls are already there.
      modalVideo.addEventListener('playing', () => {
        // Unmute only once it is really running. If the browser then stops it, the
        // controls are already there and the viewer can start it themselves.
        modalVideo.muted = false;
      }, { once: true });

      // A tick, so anything the library does to the element on open has happened first.
      requestAnimationFrame(() => {
        modalVideo.muted = true;
        playWhenReady(modalVideo);
      });
    });

    box.on('close', () => {
      // Hand the position back, then let the tile go on as it was: muted and looping.
      try {
        if (Number.isFinite(handoff) && handoff < (video.duration || Infinity)) {
          video.currentTime = handoff;
        }
      } catch (e) {
        /* a seek can fail while the tile is still loading; the tile just keeps its place */
      }
      video.muted = true;
      video.loop = true;
      video.controls = false;
      if (!REDUCED_MOTION.matches) playWhenReady(video);
    });

    box.open();
  }

  /** What the tile did before the modal existed, and still does if the modal cannot load. */
  activateInline(card) {
    const video = card.querySelector('video');
    if (!video) return;
    this.active = card;
    card.setAttribute('data-playing', 'true');
    video.muted = false;
    video.loop = false;
    video.controls = true;
    playWhenReady(video);
  }

  /** Resolves once the selector matches, or null if it never does. */
  static waitFor(selector, timeout = 3000) {
    const existing = document.querySelector(selector);
    if (existing) return Promise.resolve(existing);
    return new Promise((resolve) => {
      const started = performance.now();
      const tick = () => {
        const el = document.querySelector(selector);
        if (el) return resolve(el);
        if (performance.now() - started > timeout) return resolve(null);
        requestAnimationFrame(tick);
      };
      tick();
    });
  }

  /** Loaded once, shared by every wall on the page, and never on a page nobody clicks. */
  static loadLightbox() {
    if (window.GLightbox) return Promise.resolve(window.GLightbox);
    BeeulVideoWall._pending ||= new Promise((resolve) => {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://cdn.jsdelivr.net/npm/glightbox@3.3.1/dist/css/glightbox.min.css';
      document.head.appendChild(css);

      const js = document.createElement('script');
      js.src = 'https://cdn.jsdelivr.net/npm/glightbox@3.3.1/dist/js/glightbox.min.js';
      js.onload = () => resolve(window.GLightbox || null);
      js.onerror = () => resolve(null);
      document.head.appendChild(js);
    });
    return BeeulVideoWall._pending;
  }

  deactivate(card) {
    const video = card.querySelector('video');
    if (!video) return;

    card.removeAttribute('data-playing');
    video.muted = true;
    video.loop = true;
    video.controls = false;
    if (card === this.active) this.active = null;
  }

  /**
   * YouTube and Vimeo embeds are only inserted on demand: a third-party iframe on load
   * would cost more than the clip is worth.
   */
  loadEmbed(card, embed) {
    const src = embed.dataset.beeulEmbed;
    if (!src) return;

    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.title = embed.dataset.beeulEmbedTitle || 'Customer review video';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    iframe.loading = 'lazy';
    iframe.className = 'beeul-clip__embed';

    embed.replaceChildren(iframe);
    card.setAttribute('data-playing', 'true');
  }
}

if (!customElements.get('beeul-video-wall')) {
  customElements.define('beeul-video-wall', BeeulVideoWall);
}
